import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { LoaderComponent } from '../../../../components/loader/loader.component';
import { SharedService } from '../../../../shared.service';
import { DroReading } from '../../model/dro.model';
import { DroService } from '../../services/dro.service';
import { formatDroDate, formatDroDifference, formatDroValue } from '../../utils/dro-validation';

export interface DroReadingPopupData {
  readingId: string;
}

export interface DroReadingPopupResult {
  deleted: boolean;
}

/**
 * Lets an app lift the dialog above Bootstrap modals: the Service order popup is a .modal (z-index 1055), while the
 * Material overlay container sits at 1000 — see the matching rule in the Service styles.scss.
 */
export const DRO_READING_POPUP_PANEL_CLASS = 'dro-reading-popup-dialog';

/** The single read-only view of a reading, opened from the DRO tabs and the All Readings grid. */
export function openDroReadingPopup(dialog: MatDialog, readingId: string): Observable<DroReadingPopupResult | undefined> {
  return dialog.open<DroReadingPopupComponent, DroReadingPopupData, DroReadingPopupResult>(DroReadingPopupComponent, {
    width: '560px',
    maxWidth: '95vw',
    autoFocus: false,
    panelClass: DRO_READING_POPUP_PANEL_CLASS,
    data: { readingId }
  }).afterClosed();
}

@Component({
  selector: 'lib-dro-reading-popup',
  templateUrl: './dro-reading-popup.component.html',
  styleUrl: './dro-reading-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, LoaderComponent]
})
export class DroReadingPopupComponent implements OnInit {
  reading: DroReading | null = null;
  showLoader = false;
  deleting = false;
  canDelete: boolean;

  readonly formatDate = formatDroDate;
  readonly formatValue = formatDroValue;
  readonly formatDifference = formatDroDifference;

  constructor(
    private droService: DroService,
    private sharedService: SharedService,
    private dialogRef: MatDialogRef<DroReadingPopupComponent, DroReadingPopupResult>,
    @Inject(MAT_DIALOG_DATA) public data: DroReadingPopupData
  ) {
    this.canDelete = this.droService.canDeleteReadings();
  }

  ngOnInit(): void {
    this.showLoader = true;
    this.droService.getReadingById(this.data.readingId).subscribe({
      next: res => {
        this.showLoader = false;
        if (res.isSuccessful && res.result) {
          this.reading = res.result;
          this.canDelete = res.result.canDelete ?? this.canDelete;
        } else {
          this.sharedService.showNotification('error', res.message || 'Failed to load the reading');
        }
      },
      error: () => {
        this.showLoader = false;
        this.sharedService.showNotification('error', 'Failed to load the reading');
      }
    });
  }

  onDelete(): void {
    if (!this.reading || !this.canDelete) {
      return;
    }
    const { readingId, readingDate, serialNumber } = this.reading;
    const message = `Reading ${readingId} recorded on ${formatDroDate(readingDate)} will be permanently removed from serial ${serialNumber}. ` +
      'The day it occupies is freed, so another reading can be recorded for this serial that day.<br><br>' +
      'A reading cannot be edited, so deleting and re-recording is the only way to correct one.';

    this.sharedService.showConfirmation(message, 'Delete reading', 'Cancel', 'Delete this reading?', 'warning').then(res => {
      if (!res.isConfirmed) {
        return;
      }
      this.deleting = true;
      this.droService.deleteReading(readingId).subscribe({
        next: deleteRes => {
          this.deleting = false;
          if (deleteRes.isSuccessful) {
            this.sharedService.showNotification('success', `${readingId} deleted`);
            this.dialogRef.close({ deleted: true });
          } else {
            this.sharedService.showNotification('error', deleteRes.message || 'Failed to delete the reading');
          }
        },
        error: () => {
          this.deleting = false;
          this.sharedService.showNotification('error', 'Failed to delete the reading');
        }
      });
    });
  }

  onClose(): void {
    this.dialogRef.close({ deleted: false });
  }
}
